import * as React from 'react';
import type { DnsCheckResult, ExpectedDnsRecord } from '@mail-sv/shared';
import { labelOf } from '@mail-sv/shared';
import { Check, Copy } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table.js';
import { StatusBadge } from './status-badge.js';
import { Badge } from './badge.js';

/**
 * 契約者に案内する DNS レコードの一覧。
 *
 * 表示する期待値は必ず `buildExpectedDnsRecords()`（`@mail-sv/shared/dns.ts`）が
 * 作ったものを渡すこと。画面側で文字列を組み立てると、
 * 「画面に出ている値」と「検証ジョブが照合している値」が食い違い、
 * 契約者が正しく設定したのに永久に verified にならない、という最悪の
 * サポート案件になる。
 *
 * 検証結果（`results`）はレコードの `kind` + `name` + `value` で引く。
 */

export interface DnsRecordCheck {
  kind: string;
  name: string;
  result: DnsCheckResult;
  /** 実際に観測された値。mismatch のとき差分を見せるために使う。 */
  observed?: string | null;
  checkedAt?: string | null;
}

export interface DnsRecordTableProps {
  records: readonly ExpectedDnsRecord[];
  /** 検証ジョブの結果。未取得なら省略（結果列を出さない）。 */
  results?: readonly DnsRecordCheck[];
  /** 説明文（なぜ必要か）を各行の下に出すか。初回設定画面では true 推奨。 */
  showDescription?: boolean;
  className?: string;
}

function resultKey(kind: string, name: string): string {
  return `${kind}\u0000${name.toLowerCase()}`;
}

export function DnsRecordTable({
  records,
  results,
  showDescription = true,
  className,
}: DnsRecordTableProps) {
  const resultMap = React.useMemo(() => {
    const m = new Map<string, DnsRecordCheck>();
    for (const r of results ?? []) m.set(resultKey(r.kind, r.name), r);
    return m;
  }, [results]);

  return (
    <div className={cn('space-y-2', className)}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead scope="col" className="w-28">
              種類
            </TableHead>
            <TableHead scope="col">ホスト名</TableHead>
            <TableHead scope="col" className="w-20">
              タイプ
            </TableHead>
            <TableHead scope="col">値</TableHead>
            <TableHead scope="col" className="hidden w-20 text-right md:table-cell">
              TTL
            </TableHead>
            {results ? (
              <TableHead scope="col" className="w-24">
                確認結果
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((rec, i) => {
            const check = resultMap.get(resultKey(rec.kind, rec.name));
            return (
              <React.Fragment key={`${rec.kind}-${rec.name}-${i}`}>
                <TableRow className={showDescription ? 'border-b-0' : undefined}>
                  <TableCell className="align-top">
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-sm font-medium text-foreground">{labelOf(rec.kind)}</span>
                      {!rec.required ? (
                        <Badge variant="outline" size="sm">
                          任意
                        </Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    <CopyableValue value={rec.name} />
                  </TableCell>
                  <TableCell className="align-top">
                    <Badge variant="secondary" size="sm">
                      {rec.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="align-top">
                    <CopyableValue
                      value={rec.priority !== null ? `${rec.priority} ${rec.value}` : rec.value}
                    />
                    {check?.result === 'mismatch' && check.observed ? (
                      <p className="mt-1 break-all font-mono text-xs text-warning">
                        現在の値: {check.observed}
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell className="hidden text-right align-top tabular text-xs text-muted-foreground md:table-cell">
                    {rec.ttl}
                  </TableCell>
                  {results ? (
                    <TableCell className="align-top">
                      <StatusBadge status={check?.result ?? 'unchecked'} size="sm" dot />
                    </TableCell>
                  ) : null}
                </TableRow>
                {showDescription ? (
                  <TableRow>
                    <TableCell colSpan={results ? 6 : 5} className="pt-0 text-xs text-muted-foreground">
                      {rec.description}
                    </TableCell>
                  </TableRow>
                ) : null}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * DNS の値は 1 文字でも欠けると検証に落ちるので、必ずコピーで渡せるようにする。
 * 手打ちさせないことがサポート工数を最も減らす。
 */
function CopyableValue({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);
  const copy = React.useCallback(() => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }, [value]);

  return (
    <div className="flex items-start gap-1.5">
      <code className="min-w-0 break-all font-mono text-xs text-foreground">{value}</code>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? 'コピーしました' : `${value} をコピー`}
        title={copied ? 'コピーしました' : 'コピー'}
        className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {copied ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
      </button>
    </div>
  );
}
